// R65 — SHE RUNS THE BUSINESS OFF THESE NUMBERS, AND A FAILED LOAD READ AS ZEROS.
//
// **R47's COUNT-THE-COPIES, AND THE COUNT IS THE FINDING AGAIN.** Seven owner tabs load their own
// data; the pointer's premise was that they share a parent, and they do not — each one fetches for
// itself (measured, R25: re-measure an inherited roster before spending a round on it). FIVE of the
// seven typed the same three lines:
//
//     .then((r) => (r.status === 403 ? (onDenied(), null) : r.json()))
//     .then((d) => { if (d?.ok) setData(d); })
//     .catch(() => {})
//
// **NO `else`. NO `catch` BODY.** So a refused or dropped load leaves `data` at `null`, `loading`
// goes false, and the tab renders itself out of `data?.rooms || []`, `data?.byType || []`,
// `data?.purchases || []` — **the `|| []` on a payload field the sweep was hunting, at the one place
// on this site where the reader is the OWNER and the output is money.** The Analytics tab draws
// every room at zero visits. Financials draws no revenue. The Overview's seven tiles read "—".
// **Nothing is red anywhere**, and the honest freshness line those three tabs already carry — the
// line whose entire job is to say how old a number is — printed `"..."`.
//
// **AND TWO OF THE SEVEN WERE ALREADY HONEST, WHICH IS THE WHOLE SHAPE THIS HOUSE KEEPS FINDING.**
// `OwnerReview` has `else if (d) setNote(...)` and a real `.catch(() => setNote("That could not be
// read."))`; `OwnerGrants` has `.catch(() => setNote("The grants could not be loaded."))`. The
// capability lived on the surfaces that needed it first (W12, R26, R35, R38) and nowhere else.
//
// **THE SERVER HALF: `server/command-center-routes.js` HAS NO `try` AT ALL.** A thrown aggregate
// falls out to index.js's last-resort handler, which answers **500 with the RAW ERROR MESSAGE and no
// `ok` field** — so the client's `if (d?.ok)` guard drops it, and a Postgres error string was the
// house's only description of what went wrong on the owner's own screen. The three command routes
// answer a sentence now.
//
// One transport, `doFetch` a PARAMETER (R46), RUN against every answer these five routes can give.
//
// **THE COPY IS ENGLISH BY DESIGN AND IT IS NOT A NEW DECLARATION**: this dashboard is not in
// `src/strings.js` at all — every sentence on all seven tabs is typed in English, because the only
// person who reads it wrote them (14-B's ruling that copy follows its own SURFACE).

// The 403 is its own answer because the tabs already act on it: `onDenied()` swaps the whole
// dashboard for the not-allowed shell, which is a different act from printing a line.
//
// **R77 ADDED A FIFTH KIND, AND IT IS THE ONE THIS TRANSPORT COULD NOT SAY.** D2's law is that every
// owner mutation is POSTED TWICE — the first post changes nothing and comes back `200 { ok: false,
// needsConfirm: true, sentence }`, the second carries that identical payload plus `confirm: true`.
// Read through the four kinds above, that first answer is a **REFUSAL CARRYING NO MESSAGE**, so
// `ownerActionNote` would have printed "That did not go through. Nothing was changed." over a
// perfectly good confirm request. **That is why both screens that move money stayed hand-typed
// through R65 and R66 and grew their own defects** — the shared transport this house built for owner
// acts had no way to express this house's own owner-mutation law (R18: a deliberate `ok: false` the
// server SENT is an ANSWER, not transport noise).
export type OwnerAnswer<T> =
  | { ok: true; data: T }
  | { ok: false; kind: "denied" }
  | { ok: false; kind: "confirm"; sentence: string }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type OwnerJson = { ok?: boolean; message?: string; needsConfirm?: boolean; sentence?: string };

// The house's line where the server sent none. Declared once rather than typed per tab — five
// copies of a sentence is how two of them come to disagree.
export const OWNER_LOAD_FAILED = "That could not be read just now. The numbers below are not current.";

// R89 — **R72's LINE: A MESSAGE IS THE SERVER'S OWN WORD ONLY WHEN IT ARRIVES BESIDE `ok: false`.**
//
// **THE DEFECT IS NAMED IN THIS FILE'S OWN COMMENT AND WAS THEN WALKED PAST TWO LINES LOWER.** The
// paragraph above says index.js's last-resort 500 "carries a raw error message and no `ok` at all" —
// and both transports then read `data?.message` off exactly that payload. So a handler that throws
// without a `try` of its own hands the owner's dashboard **a raw Postgres error string**, and the
// note laws print it as though the house had composed it. R74's lesson pointed at a sentence: an
// error message is the MACHINE's word, not the server's.
//
// **IT IS SAFE TO ENFORCE BECAUSE IT WAS MEASURED FIRST, AND THE MEASUREMENT IS THE ROUND.**
// `scripts/r89-owner-refusals.mjs` enumerates every refusal in every function an `/api/owner/*`
// route reaches — 62 of them across four files — and **every single one carries `ok: false` beside
// its words.** Three pointers carried this line as unfinished for exactly this reason: a line
// enforced on a partial census is a line that silences whatever was not counted.
//
// ONE named function asked by BOTH transports, because typed twice is how the read and the act come
// to disagree about what the server said (R47).
export function serverSaidIt(data: unknown): string {
  const said = data as { ok?: unknown; message?: unknown } | null;
  if (!said || typeof said !== "object" || said.ok !== false) return "";
  return typeof said.message === "string" ? said.message : "";
}

export async function loadOwnerData<T>(url: string, doFetch: typeof fetch = fetch): Promise<OwnerAnswer<T>> {
  try {
    const res = await doFetch(url, { cache: "no-store" });
    if (res.status === 403) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "denied" }; }
    const data = (await res.json().catch(() => null)) as (OwnerJson & T) | null;
    // **`ok: true` IS WHAT EVERY ONE OF THESE ROUTES SENDS ON A SUCCESS**, so its absence is a
    // refusal rather than a payload to draw — including index.js's last-resort 500, which carries a
    // raw error message and no `ok` at all.
    if (res.ok && data && data.ok === true) return { ok: true, data: data as T };
    return { ok: false, kind: "refused", message: serverSaidIt(data) };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// **A SUCCESS SAYS NOTHING** — the numbers arriving IS the answer (R51/R53/R54/R55/R57/R58/R60/R61/
// R64 on this same question). A 403 says nothing either, because `onDenied()` has already replaced
// the page she is looking at; a line under a screen that is no longer there is noise.
export function ownerLoadNote(answer: OwnerAnswer<unknown>, failed: string = OWNER_LOAD_FAILED): string {
  if (answer.ok || answer.kind === "denied") return "";
  if (answer.kind === "refused" && answer.message) return answer.message;
  return failed || OWNER_LOAD_FAILED;
}

// ---------------------------------------------------------------------------------------------
// R66 — THE ACTS. R65 took the READS on this dashboard and named these in the RUNBOOK, because **a
// refused moderation act is worse than a refused read: she believes she has ACTED.**
//
// `OwnerLivingRoom`'s approve / hide / delete / warn and `OwnerTriage`'s decide and phase change
// were `await fetch(...).catch(() => {})` (the triage pair never read the payload at all, not even
// the status code), followed unconditionally by closing the confirm, clearing what she had typed,
// and re-reading the queue. **So a refused act leaves the row exactly where it was and says
// nothing, which does not read as a failure — it reads as a STUBBORN ROW.** She taps hide again.
//
// And the Living Room's fetch was not wrapped at all, so a thrown request rejected the handler
// before `setConfirming(null)`: **the confirm dialog stays open forever with nothing said.**
//
// One post, one answer, `doFetch` a PARAMETER (R46).
// ---------------------------------------------------------------------------------------------

// The house's line where the server sent none. An ACT gets its own sentence rather than borrowing
// the read's: "the numbers below are not current" is exactly wrong about a row that did not move.
export const OWNER_ACTION_FAILED = "That did not go through. Nothing was changed.";

export async function postOwnerAction<T>(
  url: string,
  body: unknown,
  doFetch: typeof fetch = fetch,
): Promise<OwnerAnswer<T>> {
  try {
    const res = await doFetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 403) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "denied" }; }
    const data = (await res.json().catch(() => null)) as (OwnerJson & T) | null;
    if (res.ok && data && data.ok === true) return { ok: true, data: data as T };
    // **HELD BY POSITION (R52): THE CONFIRM TEST COMES BEFORE THE REFUSAL RETURN.** Below it, D2's
    // first post is a refusal with no message, which is the house line "That did not go through.
    // Nothing was changed." printed over a request that did exactly what it was supposed to do.
    //
    // **R89: THE CONFIRM IS HELD TO THE SAME LINE, AND ON THESE TWO SCREENS IT MATTERS MOST.** D2's
    // first post is a DELIBERATE `ok: false` the server sent, so requiring it costs the confirm
    // nothing — and it means an undeclared payload carrying `needsConfirm` can never become a
    // decision about money. Every one of the four confirm sites was measured: all send `ok: false`.
    if (data && data.ok === false && data.needsConfirm === true
      && typeof data.sentence === "string" && data.sentence.trim()) {
      return { ok: false, kind: "confirm", sentence: data.sentence };
    }
    return { ok: false, kind: "refused", message: serverSaidIt(data) };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// STEP TWO OF EVERY OWNER MUTATION, AND IT IS **DERIVED FROM STEP ONE'S PAYLOAD RATHER THAN TYPED
// BESIDE IT** (R31: the crossing stops existing instead of being watched). Both money screens
// hand-typed `{ ...pending.body, confirm: true }`, and two fixtures held "the confirm re-posts the
// EXACT payload it previewed" by matching that string in a file — which proves it is TYPED and
// nothing about what is SENT. It is one line a gate RUNS now.
export function postOwnerConfirmed<T>(
  url: string,
  body: Record<string, unknown>,
  doFetch: typeof fetch = fetch,
): Promise<OwnerAnswer<T>> {
  return postOwnerAction<T>(url, { ...body, confirm: true }, doFetch);
}

// **AN ACT THAT LANDED IS NOT SILENT, AND THAT IS THE ONE PLACE THIS DIFFERS FROM EVERY NOTE LAW
// SINCE R51.** A read announces itself by the numbers appearing; a moderation act announces itself
// by a row leaving a queue — and the SERVER composes the sentence describing what it actually did
// (the D2 law: a confirm written on the client can describe something other than what the server
// then does), including the strike step, which is the whole point of a warn. Dropping that sentence
// is dropping the only account of an act recorded in an append-only ledger.
export function ownerActionNote(
  answer: OwnerAnswer<{ sentence?: string; strike?: { label?: string } | null }>,
  failed: string = OWNER_ACTION_FAILED,
): string {
  if (answer.ok) {
    const said = String(answer.data?.sentence || "").trim();
    const step = answer.data?.strike?.label;
    return said ? (step ? `${said} ${step}.` : said) : "";
  }
  if (answer.kind === "denied") return "";
  // **AND A CONFIRM SAYS NOTHING EITHER**, for the same reason a 403 does not: the answer is already
  // on screen as the alertdialog she is reading. A note beside it would be the house telling her an
  // act failed while the sentence describing that act sits above it waiting for her word.
  if (answer.kind === "confirm") return "";
  if (answer.kind === "refused" && answer.message) return answer.message;
  return failed || OWNER_ACTION_FAILED;
}

// ---------------------------------------------------------------------------------------------
// ITEM 7 GATE REPAIR — THE ADVISOR APPLICATIONS BADGE IN HER TOP BAR, ON R65's ROSTER.
//
// The tab's count is a READ, so it is held to R65 like every other owner read: THREE states, never
// two. Not yet answered draws nothing; a real number draws its badge (a real zero draws none, which
// is an answer: nobody is waiting); and a read that did not land SAYS SO with its own mark and
// sentence, because a missing badge over a failed read would read as "nobody is waiting" (R18).
// A payload that arrives `ok` but carries no number is a refusal, never a zero.
//
// The tab links to an OWNER route that redirects her seat server-side, so the shipped bundle never
// carries the console's address (her dark law for the Truth wing).
// ---------------------------------------------------------------------------------------------
export const APPLICATIONS_OPEN_PATH = "/api/owner/command/applications/open";
export const APPLICATIONS_COUNT_FAILED = "The count of new advisor applications could not be read just now.";
export type ApplicationsBadge =
  | { kind: "waiting" }
  | { kind: "count"; fresh: number }
  | { kind: "failed"; note: string };
export function applicationsBadge(answer: OwnerAnswer<{ fresh?: unknown }> | null): ApplicationsBadge {
  if (!answer) return { kind: "waiting" };
  if (answer.ok) {
    const fresh = answer.data?.fresh;
    if (typeof fresh === "number" && Number.isInteger(fresh) && fresh >= 0) return { kind: "count", fresh };
    return { kind: "failed", note: APPLICATIONS_COUNT_FAILED };
  }
  return { kind: "failed", note: ownerLoadNote(answer, APPLICATIONS_COUNT_FAILED) || APPLICATIONS_COUNT_FAILED };
}
